// Helper to format date
function formateDate(d) {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

// Clean maturity status names
function cleanMaturityState(stateStr) {
  if (!stateStr) return "Draft";
  let clean = String(stateStr).trim();
  if (clean.includes(":")) {
    clean = clean.split(":").pop();
  }
  return clean;
}

// Dynamic 3dplan URL resolution
export const get3DPlanURL = () => {
  return new Promise((resolve, reject) => {
    try {
      if (typeof window === "undefined" || !window.requirejs) {
        reject(new Error("window.requirejs is not available"));
        return;
      }
      window.requirejs(
        ["DS/i3DXCompassServices/i3DXCompassServices"],
        function (i3DXCompassServices) {
          i3DXCompassServices.getServiceUrl({
            serviceName: "3dplan",
            onComplete: function (urls) {
              const currentHost = window.location.hostname.toLowerCase();

              const result = urls.find((item) => {
                if (!item.url) return false;

                // On-Premise
                if (item.platformId === "OnPremise") {
                  return (
                    new URL(item.url).hostname.toLowerCase() === currentHost
                  );
                }

                // Cloud
                const currentTenant = currentHost
                  .split(".")[0]
                  .split("-")[0]
                  .toLowerCase();

                return item.platformId.toLowerCase() === currentTenant;
              });

              const Space3DURL = result ? result.url : null;

              console.log("Selected 3DSpace URL:", Space3DURL);

              resolve(Space3DURL);
            },
            onFailure: function (err) {
              console.error("Service 3dplan URL Error:", err);
              reject(err);
            },
          });
        }
      );
    } catch (error) {
      reject(error);
    }
  });
};

// Authenticated request via WAFData
const authenticatedRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    try {
      if (typeof window === "undefined" || !window.requirejs) {
        reject(new Error("window.requirejs is not available"));
        return;
      }
      window.requirejs(["DS/WAFData/WAFData"], function (WAFData) {
        WAFData.authenticatedRequest(url, {
          ...options,
          onComplete: function (data) {
            resolve(data);
          },
          onFailure: function (err) {
            reject(err);
          }
        });
      });
    } catch (error) {
      reject(error);
    }
  });
};

const getRequest = async (url) => {
  return authenticatedRequest(url, {
    method: "GET",
    type: "json"
  });
};

const postRequest = async (url, data) => {
  return authenticatedRequest(url, {
    method: "POST",
    type: "json",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    data: JSON.stringify(data)
  });
};

// Main function to fetch all Team Project data and convert it
export async function loadTeamProject(projectId) {
  const planUrl = await get3DPlanURL();
  const rawId = projectId.startsWith("uuid:") ? projectId.replace("uuid:", "") : projectId;
  
  // 1. Fetch Project Details via the new Swagger projects API
  const projectUrl = `${planUrl}/resources/v1/3dplan/projects/uuid:${rawId}`;
  const project = await getRequest(projectUrl);
  
  // 2. Extract referenced tasks and milestones
  // const entityRefs = project?.hasTask?.member || [];
   const entityRefs = [
   ...(project?.hasTask?.member || []),
   ...(project?.hasWorkDecomposition?.member || []),
   ];
  // 3. Fetch detailed records for each referenced task/milestone in parallel
  const detailsList = [];
  const fetchedIds = new Set();

  async function fetchEntityAndSubtasks(refs) {
    const promises = refs.map(async (ref) => {
      const id = ref.identifier;
      if (fetchedIds.has(id)) return;
      fetchedIds.add(id);

      const type = ref.type;
      let entity = null;
      try {
        if (type === "dsplan:Task") {
          entity = await getRequest(`${planUrl}/resources/v1/3dplan/tasks/${id}`);
        } else if (type === "dsplan:Milestone") {
          entity = await getRequest(`${planUrl}/resources/v1/3dplan/milestones/${id}`);
         } else if (type === "dsplan:Project") {
          entity = await getRequest(`${planUrl}/resources/v1/3dplan/projects/${id}`);
          if (entity) {
            const childRefs = [
              ...(entity?.hasTask?.member || []),
              ...(entity?.hasWorkDecomposition?.member || []),
            ];
            if (childRefs.length > 0) await fetchEntityAndSubtasks(childRefs);
          }
        }
      } catch (e) {
        console.warn(`Failed to fetch details for ${type} ${id}:`, e);
      }

      if (entity) {
        detailsList.push(entity);
        if (entity.hasSubtask && Array.isArray(entity.hasSubtask.member)) {
          await fetchEntityAndSubtasks(entity.hasSubtask.member);
        }
      }
    });
    await Promise.all(promises);
  }

  await fetchEntityAndSubtasks(entityRefs);
  
  // 4. Fetch Dependencies
  const depsUrl = `${planUrl}/v1/invoke/dsxplan:getAllConnectedDependencies`;
  const depsPayload = [
    `uuid:${rawId}`,
    {
      reset: true,
      dependencyType: "all",
      dependencyStatus: "all",
      dependencySource: "all",
      subprojectDependencies: true
    }
  ];
  const depsResponse = await postRequest(depsUrl, depsPayload);
  let dependencies = [];
  if (depsResponse && depsResponse["@value"]) {
    try {
      dependencies = JSON.parse(depsResponse["@value"]);
    } catch (e) {
      console.error("Failed to parse Team Project dependencies:", e);
    }
  }
  
  // 5. Convert to Diagram
  const nodes = [];
  const nodeById = {};
  const edges = [];
  
  const parentMap = {};
  detailsList.forEach(entity => {
    if (entity.hasSubtask && Array.isArray(entity.hasSubtask.member)) {
      entity.hasSubtask.member.forEach(m => {
        parentMap[m.identifier] = entity.identifier;
      });
    }
    if (entity.hasTask && Array.isArray(entity.hasTask.member)) {
     entity.hasTask.member.forEach(m => {
       parentMap[m.identifier] = entity.identifier;
     });
    }
    if (entity.hasWorkDecomposition && Array.isArray(entity.hasWorkDecomposition.member)) {
      entity.hasWorkDecomposition.member.forEach(m => {
        parentMap[m.identifier] = entity.identifier;
      });
    }
  });
  
  // Create task and milestone nodes
  detailsList.forEach(entity => {
    const id = entity.identifier;
    const type = entity.type; // "dsplan:Task" or "dsplan:Milestone"
    
    // Parse assignees strictly from hasAssignee collection
    let assignees = [];
    if (entity.hasAssignee && Array.isArray(entity.hasAssignee.member)) {
      assignees = entity.hasAssignee.member.map(m => m.accountName).filter(Boolean);
    }
    
    // Parse deliverables/attachments
    const deliverables = [];
    if (entity.hasAttachment && Array.isArray(entity.hasAttachment.member)) {
      entity.hasAttachment.member.forEach(m => {
        deliverables.push({
          id: m.identifier,
          title: m.title || m.name || `Attachment (${m.service || "3DSpace"})`,
          service: m.service || "3DSpace"
        });
      });
    }
    if (entity.hasDeliverable && Array.isArray(entity.hasDeliverable.member)) {
      entity.hasDeliverable.member.forEach(m => {
        deliverables.push({
          id: m.identifier,
          title: m.title || m.name || `Deliverable (${m.service || "3DDrive"})`,
          service: m.service || "3DDrive"
        });
      });
    }
    
    // Extract dates
    const startStr = entity.estimatedStartDate || "";
    const endStr = entity.estimatedEndDate || entity.suggestedDate || "";
    const subtitle = type === "dsplan:Milestone" ? "Milestone"
    : type === "dsplan:Project" ? "Project"
    : "Task";
    
    const childrenIds = [
      ...(entity.hasSubtask?.member || []),
      ...(entity.hasTask?.member || []),
      ...(entity.hasWorkDecomposition?.member || []),
    ].map(m => m.identifier);
    const parentId = parentMap[id] || null;
    
    const node = {
      id: id,
      title: entity.title || entity.name || "",
      subtitle: subtitle,
      status: entity.hasMaturity || "",
      maturity: cleanMaturityState(entity.hasMaturity),
      assignees: assignees,
      dueDate: formateDate(endStr),
      rawDueDate: endStr,
      rawActualFinishDate: entity.actualEndDate || "",
      startDate: formateDate(startStr),
      width: 220,
      height: 100,
      raw: entity,
      totalFloat: 0,
      parentId: parentId,
      childrenIds: childrenIds,
      hasChildren: childrenIds.length > 0,
      isInitial: !parentId,
      hierarchyDepth: 0, // Computed below
      deliverables: deliverables,
    };
    
    nodes.push(node);
    nodeById[id] = node;
    
    if (parentId) {
      edges.push({ from: parentId, to: id, type: "hierarchy" });
    }
  });
  
  // Compute depth
  nodes.forEach(node => {
    let depth = 0;
    let curr = node;
    while (curr && curr.parentId) {
      depth++;
      curr = nodeById[curr.parentId];
    }
    node.hierarchyDepth = depth;
  });
  
  // Add dependency edges
  dependencies.forEach(dep => {
    const fromId = dep["dsxplan:from"]?.["@id"];
    const toId = dep["dsxplan:to"]?.["@id"];
    if (fromId && toId && nodeById[fromId] && nodeById[toId]) {
      edges.push({ from: fromId, to: toId, type: "dependency" });
    }
  });
  
  return { nodes, edges, nodeById };
}

// ─────────────────────────────────────────────────────────────────────────────
// 🚀 Dynamic 3DPlan REST Helpers (For Future CRUD & Graph Expansions)
// ─────────────────────────────────────────────────────────────────────────────

// ── Tasks ──
export function extractUUID(id) {
  if (!id) return id;
  return id.replace(/[<>]/g, "").replace(/^uuid:/, "");
}

export async function getTeamTask(taskId) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/tasks/uuid:${extractUUID(taskId)}`;
  return getRequest(url);
}

export async function updateTeamTask(taskId, attributes) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/tasks/uuid:${extractUUID(taskId)}`;
  return authenticatedRequest(url, {
    method: "PATCH",
    type: "json",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    data: JSON.stringify(attributes)
  });
}

export async function deleteTeamTask(taskId) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/tasks/uuid:${extractUUID(taskId)}`;
  return authenticatedRequest(url, {
    method: "DELETE",
    type: "json",
    headers: {
      "X-Requested-With": "XMLHttpRequest"
    }
  });
}

// ── Milestones ──
export async function getTeamMilestone(milestoneId) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/milestones/uuid:${extractUUID(milestoneId)}`;
  return getRequest(url);
}

export async function updateTeamMilestone(milestoneId, attributes) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/milestones/uuid:${extractUUID(milestoneId)}`;
  return authenticatedRequest(url, {
    method: "PATCH",
    type: "json",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    data: JSON.stringify(attributes)
  });
}

// ── Dependencies ──
export async function getTeamDependency(depId) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/dependencies/uuid:${extractUUID(depId)}`;
  return getRequest(url);
}

export async function createTeamDependency(fromId, toId, constraintType = "Before") {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/dependencies`;
  const payload = {
    from: `uuid:${extractUUID(fromId)}`,
    to: `uuid:${extractUUID(toId)}`,
    constraintType: constraintType
  };
  return postRequest(url, payload);
}

// ── Project Members ──
export async function getTeamProjectMembers(projectId) {
  const planUrl = await get3DPlanURL();
  const url = `${planUrl}/resources/v1/3dplan/projects/uuid:${extractUUID(projectId)}/members`;
  return getRequest(url);
}

